/**
 * PoiLegend - Легенда с фильтрами для точек интереса (POI)
 *
 * Мигрирована на новую систему легенд (Этап 2)
 * Использует:
 * - LegendRenderer для базовой визуализации легенды
 * - Кастомный рендер с чекбоксами для фильтрации
 *
 * Две группы тоглов (DA-7087):
 * - «Категории POI» — фильтр по category (POI_STYLE)
 * - «Источник данных» — фильтр по source: OSM / AllThePlaces.xyz (POI_SOURCE_STYLE)
 *
 * Структура остаётся совместимой с Redux и фильтрами
 */

import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import classnames from 'classnames';
import { connect } from 'react-redux';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { onChangePoiFilter, onChangePoiSourceFilter } from '../../../ac/onChangeMapFilters';
import { POI_LEGEND } from '../../../constants/legends';
import { POI_STYLE, POI_SOURCE_STYLE } from '../../../constants/layerStyles';
import { getStyleFilterItems } from '../../../constants/filterStylesResolver';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import styles from '../FilterSidebar.module.css';
import { LegendIconRenderer } from '../../LegendRenderer/LegendIconRenderer';

/**
 * Группа тоглов фильтра: иконка + подпись + переключатель.
 * selected — массив выбранных значений; onChange — обработчик чекбокса.
 */
const ToggleGroup = ({ items, selected, onChange, name, iconOpacity }) => (
    <div className={styles.checkboxIconGrid}>
        {items.map(item => (
            <Fragment key={item.id || item.value}>
                <LegendIconRenderer
                    type={item.icon || POI_LEGEND.icon}
                    color={item.color || '#888'}
                    size="2vw"
                    opacity={iconOpacity}
                />
                <label htmlFor={item.value || item.id}>{i18n._(item.label)}</label>
                <div>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            name={name}
                            id={item.value || item.id}
                            checked={selected.includes(item.value || item.id)}
                            value={item.value || item.id}
                            onChange={onChange}
                        />
                        <span className={classnames(styles.sliderRound, styles.slider)} />
                    </label>
                </div>
            </Fragment>
        ))}
    </div>
);

ToggleGroup.propTypes = {
    items: PropTypes.array.isRequired,
    selected: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    iconOpacity: PropTypes.number,
};

ToggleGroup.defaultProps = {
    iconOpacity: 0.8,
};

/**
 * PoiLegend с фильтрами - специализированная версия легенды POI
 * Отображает чекбоксы для фильтрации категорий POI и источника данных
 * Использует Filter Templates System - items подтягиваются из стилей через resolver
 */
const PoiFilterComponent = ({
    mapFilters,
    onChangePoiFilter,
    onChangePoiSourceFilter,
}) => {
    const categoryItems = getStyleFilterItems(POI_STYLE, 'categories');
    const sourceItems = getStyleFilterItems(POI_SOURCE_STYLE, 'sources');

    return (
        <div>
            <div className={styles.legHead}>{t`Категории POI:`}</div>
            <ToggleGroup
                items={categoryItems}
                selected={mapFilters.poiStatuses}
                onChange={onChangePoiFilter}
                name="poi"
            />
            <div className={styles.legHead}>{t`Источник данных:`}</div>
            <ToggleGroup
                items={sourceItems}
                selected={mapFilters.poiSources}
                onChange={onChangePoiSourceFilter}
                name="poiSource"
                iconOpacity={0.5}
            />
        </div>
    );
};

PoiFilterComponent.propTypes = {
    mapFilters: PropTypes.object.isRequired,
    onChangePoiFilter: PropTypes.func.isRequired,
    onChangePoiSourceFilter: PropTypes.func.isRequired,
};

const mapStateToProps = ({ mapFilters }) => ({ mapFilters });
const ConnectedComponent = connect(mapStateToProps, { onChangePoiFilter, onChangePoiSourceFilter })(PoiFilterComponent);
export const PoiLegend = withDisplayedLayersCheck(ConnectedComponent, t`POI`);
